import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, Car, ChevronDown } from "lucide-react";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leistungenOpen, setLeistungenOpen] = useState(false);
  const [einsatzgebieteOpen, setEinsatzgebieteOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const leistungen = [
    { name: "Unfallgutachten", slug: "unfallgutachten" },
    { name: "Haftpflichtgutachten", slug: "haftpflichtgutachten" },
    { name: "Kasko-Schaden", slug: "kasko-schaden" },
    { name: "Kostenvoranschlag", slug: "kostenvoranschlag" },
    { name: "Wertgutachten", slug: "wertgutachten" },
    { name: "Oldtimer-Gutachten", slug: "oldtimer-gutachten" },
  ];

  const einsatzgebiete = [
    { name: "Hamburg", slug: "hamburg" },
    { name: "Schleswig-Holstein", slug: "schleswig-holstein" },
    { name: "Pinneberg", slug: "pinneberg" },
    { name: "Elmshorn", slug: "elmshorn" },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header shadow-md" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <div className="w-12 h-12 bg-[#AA6622] rounded-sm flex items-center justify-center">
              <Car className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="font-bold text-xl text-black font-['Outfit']">Kfz Gutachter</span>
              <span className="block text-sm text-[#4A4A4A]">Schmeißer</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Leistungen Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setLeistungenOpen(true)}
              onMouseLeave={() => setLeistungenOpen(false)}
            >
              <Link
                to="/leistungen"
                className={`flex items-center space-x-1 font-medium transition-colors py-6 ${
                  location.pathname.startsWith('/leistungen') ? 'text-[#AA6622]' : 'text-[#4A4A4A] hover:text-[#AA6622]'
                }`}
                data-testid="nav-leistungen"
              >
                <span>Leistungen</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${leistungenOpen ? 'rotate-180' : ''}`} />
              </Link>
              {leistungenOpen && (
                <div className="absolute top-full left-0 w-56 bg-white shadow-lg rounded-sm border border-[#E5E5E5] py-2 animate-fade-in z-50">
                  {leistungen.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/leistungen/${item.slug}`}
                      className="block px-4 py-2 text-[#4A4A4A] hover:text-[#AA6622] hover:bg-[#F7F7F7] transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Einsatzgebiete Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setEinsatzgebieteOpen(true)}
              onMouseLeave={() => setEinsatzgebieteOpen(false)}
            >
              <Link
                to="/einsatzgebiete"
                className={`flex items-center space-x-1 font-medium transition-colors py-6 ${
                  location.pathname.startsWith('/einsatzgebiete') ? 'text-[#AA6622]' : 'text-[#4A4A4A] hover:text-[#AA6622]'
                }`}
                data-testid="nav-einsatzgebiete"
              >
                <span>Einsatzgebiete</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${einsatzgebieteOpen ? 'rotate-180' : ''}`} />
              </Link>
              {einsatzgebieteOpen && (
                <div className="absolute top-full left-0 w-56 bg-white shadow-lg rounded-sm border border-[#E5E5E5] py-2 animate-fade-in z-50">
                  {einsatzgebiete.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/einsatzgebiete/${item.slug}`}
                      className="block px-4 py-2 text-[#4A4A4A] hover:text-[#AA6622] hover:bg-[#F7F7F7] transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                  <div className="border-t border-[#E5E5E5] mt-2 pt-2">
                    <Link
                      to="/einsatzgebiete"
                      className="block px-4 py-2 text-[#AA6622] font-medium hover:bg-[#F7F7F7] transition-colors"
                    >
                      Alle Einsatzgebiete →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/ueber-uns"
              className={`font-medium transition-colors ${
                location.pathname === '/ueber-uns' ? 'text-[#AA6622]' : 'text-[#4A4A4A] hover:text-[#AA6622]'
              }`}
              data-testid="nav-ueber-uns"
            >
              Über uns
            </Link>

            <Link
              to="/ratgeber"
              className={`font-medium transition-colors ${
                location.pathname.startsWith('/ratgeber') ? 'text-[#AA6622]' : 'text-[#4A4A4A] hover:text-[#AA6622]'
              }`}
              data-testid="nav-ratgeber"
            >
              Ratgeber
            </Link>

            <Link
              to="/faq"
              className={`font-medium transition-colors ${
                location.pathname === '/faq' ? 'text-[#AA6622]' : 'text-[#4A4A4A] hover:text-[#AA6622]'
              }`}
              data-testid="nav-faq"
            >
              FAQ
            </Link>

            <Link
              to="/kontakt"
              className={`font-medium transition-colors ${
                location.pathname === '/kontakt' ? 'text-[#AA6622]' : 'text-[#4A4A4A] hover:text-[#AA6622]'
              }`}
              data-testid="nav-kontakt"
            >
              Kontakt
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:+491748936514"
              className="flex items-center space-x-2 text-[#AA6622] font-bold hover:text-[#8e541b] transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-5 h-5" />
              <span>0174 893 6514</span>
            </a>
            <a
              href="https://wa.me/+491748936514"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white px-4 py-2 rounded-sm font-bold flex items-center space-x-2 hover:bg-[#20bd5a] transition-colors"
              data-testid="header-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#E5E5E5] animate-fade-in bg-white" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-2">
              <Link to="/leistungen" className="text-[#4A4A4A] hover:text-[#AA6622] font-medium py-2 px-2">
                Leistungen
              </Link>
              {leistungen.map((item) => (
                <Link
                  key={item.slug}
                  to={`/leistungen/${item.slug}`}
                  className="text-[#4A4A4A] hover:text-[#AA6622] py-2 px-6 text-sm"
                >
                  {item.name}
                </Link>
              ))}
              <Link to="/einsatzgebiete" className="text-[#4A4A4A] hover:text-[#AA6622] font-medium py-2 px-2">
                Einsatzgebiete
              </Link>
              <Link to="/ueber-uns" className="text-[#4A4A4A] hover:text-[#AA6622] font-medium py-2 px-2">
                Über uns
              </Link>
              <Link to="/ratgeber" className="text-[#4A4A4A] hover:text-[#AA6622] font-medium py-2 px-2">
                Ratgeber
              </Link>
              <Link to="/faq" className="text-[#4A4A4A] hover:text-[#AA6622] font-medium py-2 px-2">
                FAQ
              </Link>
              <Link to="/kontakt" className="text-[#4A4A4A] hover:text-[#AA6622] font-medium py-2 px-2">
                Kontakt
              </Link>
              <div className="pt-4 flex flex-col space-y-3 px-2">
                <a
                  href="tel:+491748936514"
                  className="flex items-center justify-center space-x-2 text-[#AA6622] font-bold py-3 border-2 border-[#AA6622] rounded-sm"
                >
                  <Phone className="w-5 h-5" />
                  <span>Anrufen</span>
                </a>
                <a
                  href="https://wa.me/+491748936514"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 bg-[#25D366] text-white font-bold py-3 rounded-sm"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
